import { Component, computed, inject, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../_generated';
import { SkillCardEditCapability } from './assistants.helper';
import * as fromAssistantIcon from './assistants.icon.helper';

type MoreOptionsDropdownMenuOption = fromSeriousSystem.DropdownMenuOption & {
  type:
    | 'start-chat'
    | 'add-to-favorites'
    | 'remove-from-favorites'
    | 'edit'
    | 'delete';
  action: () => void;
  sortOrder: number;
  disabled: boolean;
  error: boolean;
};

@Component({
  selector: 'squadbox-assistant-card',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    fromSeriousSystem.SpinnerDirective,
    TranslateModule,
    MatTooltipModule,
  ],
  template: `
    <div
      role="button"
      [attr.tabindex]="status() === 'ready' ? 0 : -1"
      [attr.aria-disabled]="status() === 'ready' ? null : true"
      [class.grid-template-area]="'logo'"
      class="
        relative
        flex items-center justify-center
        size-12
        text-primary-300
      "
      [class.cursor-pointer]="status() === 'ready'"
      (click)="handleClickRowCTA()"
      (keydown.enter)="handleClickRowCTA()"
      (keydown.space)="handleClickRowCTA(); $event.preventDefault()"
    >
      <!-- Status badge -->
      @if (status() === 'processing' || status() === 'failed') {
        <div
          class="
            absolute top-0 right-0 transform -translate-x-[5%] translate-y-[5%]
            flex items-center justify-center
            size-6 p-1 rounded-full border-2 border-shades-white
          "
          [class.bg-primary-500]="status() === 'processing'"
          [class.bg-error-500]="status() === 'failed'"
        >
          @if (status() === 'processing') {
            <div sdSpinner color="white" size="xs" class="h-full"></div>
          } @else if (status() === 'failed') {
            <svg
              sdUseUIBasicIcon="warning-filled"
              class="size-3 text-shades-white"
            ></svg>
          }
        </div>
      }
      <!-- One dispatcher for every icon set — see assistant-logo.component.ts
           for why the switch went away and why the two branches below cannot be
           collapsed into one. -->
      @let logoDescriptor =
        fromAssistantIcon.getAssistantIconDescriptor(logo());
      @if (logoDescriptor) {
        <svg [sdUseIcon]="logoDescriptor" class="size-6"></svg>
      } @else if (logo()) {
        <img [src]="logo()" alt="Assistant Logo" class="size-6" />
      }
    </div>
    <div
      role="button"
      [attr.tabindex]="status() === 'ready' ? 0 : -1"
      [attr.aria-disabled]="status() === 'ready' ? null : true"
      [class.grid-template-area]="'text'"
      class="flex flex-col grow gap-1 overflow-hidden"
      [class.cursor-pointer]="status() === 'ready'"
      (click)="handleClickRowCTA()"
      (keydown.enter)="handleClickRowCTA()"
      (keydown.space)="handleClickRowCTA(); $event.preventDefault()"
    >
      <div
        class="
          flex flex-col tablet-landscape:flex-row tablet-landscape:items-center
          gap-1 tablet-landscape:gap-2
          font-semibold text-neutral-700
        "
      >
        <span class="typo-p3 overflow-hidden text-ellipsis">{{ name() }}</span>
        @if (isDefaultChat()) {
          <!-- [unverified: match Figma] reuses the Admin Skills page badge -->
          <span
            class="flex items-center h-6 px-3 rounded-full bg-primary-100 typo-caption leading-4 font-semibold text-primary-300 whitespace-nowrap w-fit"
          >
            {{ 'ASSISTANTS.CARD.DEFAULT_TAG' | translate }}
          </span>
        }
      </div>
      <div
        class="typo-caption line-clamp-2"
        [class.text-neutral-500]="status() !== 'failed'"
        [class.text-error-600]="status() === 'failed'"
      >
        @if (status() === 'processing') {
          {{ 'ASSISTANTS.CARD.PROCESSING_STATUS_DESCRIPTION' | translate }}
        } @else if (status() === 'failed') {
          {{ 'ASSISTANTS.CARD.FAILED_STATUS_DESCRIPTION' | translate }}
        } @else {
          {{ description() }}
        }
      </div>
    </div>
    <div
      [class.grid-template-area]="'action'"
      class="pl-0 tablet-landscape:pl-10"
    >
      <div class="flex items-center gap-2">
        @if (status() !== 'failed' && !isMobile()) {
          @if (editCapability() !== 'none') {
            <!-- Edit skill (owner) or Edit knowledge (knowledge manager) —
                 replaces the start-new-conversation icon; the row click now
                 starts a conversation (SB-3325 PR-6). One button element for
                 both, so the owner's pencil keeps the same DOM node, focus
                 order and tooltip timing (SB-3650 / KD-2). -->
            <button
              [matTooltip]="editTooltipKey() | translate"
              [attr.aria-label]="editTooltipKey() | translate"
              matTooltipPosition="above"
              [matTooltipDisabled]="status() !== 'ready'"
              sdIconButton
              color="neutral"
              variant="icon"
              size="xs"
              [disabled]="status() === 'processing'"
              (click)="handleEditCTA()"
            >
              <svg sdUseUIBasicIcon="edit" class="text-neutral-400"></svg>
            </button>
          }
          @if (!isAdded() && !isMobile()) {
            <!-- Add to favorites -->
            <button
              [matTooltip]="'ASSISTANTS.CARD.ADD_ASSISTANT' | translate"
              matTooltipPosition="above"
              [matTooltipDisabled]="status() !== 'ready'"
              sdIconButton
              color="neutral"
              variant="icon"
              size="xs"
              [disabled]="status() === 'processing'"
              (click)="addClicked.emit()"
            >
              <svg sdUseUIBasicIcon="star" class="text-neutral-400"></svg>
            </button>
          } @else {
            <!-- Remove from favorites -->
            <button
              [matTooltip]="'ASSISTANTS.CARD.REMOVE_ASSISTANT' | translate"
              matTooltipPosition="above"
              [matTooltipDisabled]="status() !== 'ready'"
              sdIconButton
              color="primary"
              variant="icon"
              size="xs"
              [disabled]="status() === 'processing'"
              (click)="removeClicked.emit()"
            >
              <svg sdUseUIBasicIcon="star-filled" class="opacity-50"></svg>
            </button>
          }
        }
        <!-- More options — shown on mobile (holds start-chat/favorite too) and,
             on desktop, only for skills the current user owns (SB-3325 PR-6).
             The trigger and its menu share one @if so the #dropdown template
             ref stays in scope; the menu host is positioned out of the flex
             flow so its mobile drawer does not add an extra gap. -->
        @if (isMobile() || isAssistantOwnedByAuthenticatedUser()) {
          <button
            sdIconButton
            variant="icon"
            color="neutral"
            size="xs"
            [sdDropdownMenuTrigger]="dropdown"
          >
            <svg sdUseUIBasicIcon="more-horizontal"></svg>
          </button>
          <sd-dropdown-menu
            #dropdown
            class="absolute"
            [options]="dropdownMenuOptions()"
          ></sd-dropdown-menu>
        }
      </div>
    </div>
  `,
  styles: [
    `
      @reference '../../../styles.css';

      :host {
        @apply grid grid-cols-[min-content_auto_min-content] gap-2.5 tablet-portrait:gap-4 items-center;
        grid-template-areas: 'logo text action';
      }
    `,
  ],
})
export class AssistantCardComponent {
  private readonly translateService = inject(TranslateService);

  public readonly logo = input.required<string>();
  public readonly name = input.required<string>();
  public readonly description = input.required<string>();
  public readonly userId = input.required<string>();
  public readonly ownedBy = input.required<string>();
  public readonly status =
    input.required<fromGenerated.AssistantView.StatusEnum>();
  public readonly isAdded = input(false);
  /** The default chat skill, shown to a super_admin with a tag (KD-12). */
  public readonly isDefaultChat = input(false);
  public readonly isMobile = input.required<boolean>();
  /**
   * Which pencil this viewer gets, decided by the page (SB-3650 / KD-2).
   *
   * Required with no default, and the page joins it to the row it belongs to
   * rather than looking it up by uuid: `noUncheckedIndexedAccess` is set in no
   * tsconfig here, so a missed `Record` lookup would reach this input as
   * `undefined` with nothing red.
   */
  public readonly editCapability = input.required<SkillCardEditCapability>();
  /** The pencil's tooltip key, trait-derived (SB-3650 / KD-17). */
  public readonly editTooltipKey = input.required<string>();

  public readonly editClicked = output();
  /** SB-3650. The non-owner branch; nothing renders the modal until T3. */
  public readonly editKnowledgeClicked = output();
  public readonly deleteClicked = output();
  public readonly newConvoClicked = output();
  public readonly addClicked = output();
  public readonly removeClicked = output();
  protected readonly fromAssistantIcon = fromAssistantIcon;

  private get availableOptions(): Record<
    string,
    MoreOptionsDropdownMenuOption
  > {
    return {
      START_CHAT: {
        type: 'start-chat',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.START_CHAT',
        ) as string,
        icon: { type: 'navigation', icon: 'new-chat' },
        action: () => this.newConvoClicked.emit(),
        sortOrder: 1,
        disabled: false,
        error: false,
      },
      EDIT: {
        type: 'edit',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.EDIT',
        ) as string,
        icon: { type: 'uiBasic', icon: 'edit' },
        action: () => this.editClicked.emit(),
        sortOrder: 2,
        disabled: false,
        error: false,
      },
      ADD_TO_FAVORITES: {
        type: 'add-to-favorites',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.ADD_TO_FAVORITES',
        ) as string,
        icon: { type: 'uiBasic', icon: 'star' },
        action: () => this.addClicked.emit(),
        sortOrder: 4,
        disabled: false,
        error: false,
      },
      REMOVE_FROM_FAVORITES: {
        type: 'remove-from-favorites',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.REMOVE_FROM_FAVORITES',
        ) as string,
        icon: { type: 'uiBasic', icon: 'star' },
        action: () => this.removeClicked.emit(),
        sortOrder: 5,
        disabled: false,
        error: false,
      },
      DELETE: {
        type: 'delete',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.DELETE',
        ) as string,
        icon: { type: 'uiBasic', icon: 'delete' },
        action: () => this.deleteClicked.emit(),
        sortOrder: 7,
        disabled: false,
        error: true,
      },
    };
  }

  /**
   * Ownership, for the DROPDOWN MENU only (SB-3650).
   *
   * It stopped gating the pencil — `editCapability` does that now, decided by
   * the page — but it still gates the three-dots trigger and the EDIT /
   * DELETE entries. **Do not replace these with `editCapability`:**
   * those are ownership questions, not capability questions, and a knowledge
   * manager who inherited them would get DELETE on a skill they do not own.
   * `userId` and `ownedBy` are therefore still required inputs, not leftovers.
   */
  protected readonly isAssistantOwnedByAuthenticatedUser = computed(
    () => this.ownedBy() === this.userId(),
  );

  public readonly dropdownMenuOptions = computed(() => {
    const options: MoreOptionsDropdownMenuOption[] = [];
    const available = this.availableOptions;

    // Owner-only management actions (SB-3325 PR-6): non-owners no longer get a
    // desktop menu at all, and on mobile keep only start-chat + favorite below.
    if (this.isAssistantOwnedByAuthenticatedUser()) {
      // Desktop owners edit through the adjacent pencil; keep EDIT in the
      // mobile menu because the external action buttons are hidden there.
      // An owner with no pencil (a reserved trait) gets no EDIT either.
      if (this.isMobile() && this.editCapability() === 'builder') {
        options.push(available['EDIT']);
      }
      options.push(available['DELETE']);
    }

    // On mobile the start-chat and add/remove-from-favorites actions live
    // inside the dropdown because the external icon buttons are hidden.
    if (this.isMobile()) {
      options.push(
        available['START_CHAT'],
        this.isAdded()
          ? available['REMOVE_FROM_FAVORITES']
          : available['ADD_TO_FAVORITES'],
      );
    }

    return options.sort((a, b) => a.sortOrder - b.sortOrder);
  });

  /**
   * The pencil's click, branched by capability (SB-3650 / KD-2).
   *
   * Branching here rather than in the template keeps ONE button element, so the
   * owner's pencil stays the same DOM node with the same focus order and
   * tooltip timing. This is also where the map's Phase 2 seam lands — though
   * not necessarily as a third branch: a non-owner holding a full-edit grant
   * may simply BE `'builder'`, which routes to `handleEditAssistantCTA` and the
   * builder modal. Whether that modal tolerates a non-owner is Phase 2's open
   * question, and it is asked here rather than answered.
   *
   * **Every branch is explicit, and `'none'` emits nothing.** A default arm
   * falling through to `editClicked` would make the LEAST privileged capability
   * reach the MOST privileged output — `handleEditAssistantCTA` opens the
   * builder modal with no ownership re-check of its own. The `@if` above hides
   * the button at `'none'` today, so it is unreachable; a future caller that
   * does not go through that `@if` (a keyboard shortcut, a mobile menu entry)
   * would not be.
   */
  public handleEditCTA(): void {
    switch (this.editCapability()) {
      case 'knowledge':
        this.editKnowledgeClicked.emit();
        break;
      case 'builder':
        this.editClicked.emit();
        break;
      case 'none':
        break;
    }
  }

  /**
   * Clicking (or keyboard-activating) a card row starts a new conversation with
   * the skill (SB-3325 PR-6), for owned and shared skills alike. Gated on
   * `ready` — mirrors the availability of the old start-new-conversation icon,
   * which was disabled while processing and hidden when failed.
   */
  public handleClickRowCTA(): void {
    if (this.status() !== 'ready') {
      return;
    }

    this.newConvoClicked.emit();
  }
}
